import apiClient from '../client';
import type { ReferralCodeSchema } from '@appTypes';

// Referral Codes API
export type { ReferralCodeSchema } from '@appTypes';

/**
 * Fetch all referral codes (non-paginated)
 */
export async function fetchReferralCodes(): Promise<{ [key: string]: ReferralCodeSchema } | undefined> {
  const response: any = await apiClient.get('/referral/codes', {
    params: { paginated: false },
  });
  return response.data;
}

/**
 * Fetch paginated referral codes
 */
export async function fetchPaginatedReferralCodes(params?: { lastDocumentId?: string }): Promise<{ data: { [key: string]: ReferralCodeSchema }; lastDocumentId?: string } | undefined> {
  const response: any = await apiClient.get('/referral/codes', {
    params: { paginated: true, lastDocumentId: params?.lastDocumentId },
  });
  return { data: response.data, lastDocumentId: response.lastDocumentId };
}

/**
 * Fetch referral codes by influencer ID
 */
export async function fetchReferralCodeByInfluencerId(params: { influencerId: string }): Promise<{ [key: string]: ReferralCodeSchema } | undefined> {
  const response: any = await apiClient.get(`/referral/codes/by-influencer/${params.influencerId}`);
  return response.data;
}

/**
 * Get a specific referral code by ID
 */
export async function getReferralCodeById(params: { referralCodeId: string }): Promise<ReferralCodeSchema | undefined> {
  const response: any = await apiClient.get(`/referral/codes/${params.referralCodeId}`);
  return response.data;
}

// Note: The backend uses 'referralCode' as the primary key, not 'referralCodeId'
function getReferralCodeId(referralCode: ReferralCodeSchema): string {
  return referralCode.referralCode;
}

/**
 * Create a new referral code
 */
export async function addReferralCode(params: {
  referralCode: string;
  influencerId: string;
  bonusValueForCodeUsage: number;
  costPerNewUserInDinars: number;
}): Promise<ReferralCodeSchema> {
  const response: any = await apiClient.post('/referral/codes', params);
  return response.data;
}

/**
 * Update a referral code
 */
export async function updateReferralCode(params: {
  referralCode: ReferralCodeSchema;
  newData: Partial<ReferralCodeSchema>;
}): Promise<ReferralCodeSchema> {
  const codeId = getReferralCodeId(params.referralCode);
  const response: any = await apiClient.put(`/referral/codes/${codeId}`, params);
  return response.data;
}

/**
 * Delete a referral code
 */
export async function deleteReferralCode(params: { referralCode: ReferralCodeSchema }): Promise<void> {
  const codeId = getReferralCodeId(params.referralCode);
  await apiClient.delete(`/referral/codes/${codeId}`, {
    data: params,
  });
}

/**
 * Check if a referral code can be deleted
 */
export async function canDeleteReferralCode(params: { referralCode: ReferralCodeSchema }): Promise<boolean> {
  const codeId = getReferralCodeId(params.referralCode);
  const response: any = await apiClient.get(`/referral/codes/${codeId}/can-delete`);
  return response.data;
}

/**
 * Toggle active status of a referral code
 */
export async function toggleActiveReferralCode(params: { referralCode: ReferralCodeSchema }): Promise<void> {
  const codeId = getReferralCodeId(params.referralCode);
  await apiClient.post(`/referral/codes/${codeId}/toggle-active`, params);
}
